import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { getArticle, updateArticle, uploadFile } from "../../lib/api";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft, Upload, Save, Eye, Send, Loader2, Bot, User,
  Image as ImageIcon, FileText, Wand2, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Notícias", "Shows", "Lançamentos", "Artistas", "Top Hits"];

export default function EditArticle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const chatEndRef = useRef(null);
  const [form, setForm] = useState({
    title: "", excerpt: "", content: "", category: "Notícias",
    image_url: "", source_url: "", source_name: "", status: "draft",
  });

  const getToken = () => localStorage.getItem("token");

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await getArticle(id);
        const article = response.data;
        let plainContent = article.content || "";
        plainContent = plainContent.replace(/<p>/g, "").replace(/<\/p>/g, "\n\n").replace(/<br>/g, "\n");
        plainContent = plainContent.replace(/<[^>]+>/g, "").trim();
        setForm({
          title: article.title || "", excerpt: article.excerpt || "",
          content: plainContent, category: article.category || "Notícias",
          image_url: article.image_url || "", source_url: article.source_url || "",
          source_name: article.source_name || "", status: article.status || "draft",
        });
      } catch {
        toast.error("Erro ao carregar matéria");
        navigate("/admin/articles");
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id, navigate]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await uploadFile(file);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${response.data.path}`;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Imagem enviada!");
    } catch {
      toast.error("Erro ao enviar imagem");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (status) => {
    if (!form.title || !form.content) {
      toast.error("Preencha o título e o conteúdo");
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...form, status,
        content: `<p>${form.content.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>")}</p>`,
      };
      await updateArticle(id, data);
      toast.success(status === "published" ? "Matéria publicada!" : "Rascunho salvo!");
      if (status === "published") navigate("/admin/articles");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm("Descartar esta matéria permanentemente? Esta ação não pode ser desfeita.")) return;
    try {
      await axios.delete(`${API}/articles/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success("Matéria descartada");
      navigate("/admin/articles");
    } catch (err) {
      console.error("Discard error", err);
      toast.error("Erro ao descartar matéria");
    }
  };

  const sendAiInstruction = async () => {
    if (!chatInput.trim() || aiLoading) return;
    const instruction = chatInput.trim();
    setChatInput("");
    setChatHistory(prev => [...prev, { role: "user", text: instruction }]);
    setAiLoading(true);

    try {
      const response = await axios.post(
        `${API}/articles/${id}/ai-edit`,
        { instruction },
        { headers: { Authorization: `Bearer ${getToken()}` }, timeout: 120000 }
      );

      if (response.data.success) {
        const updated = response.data.article;
        let plainContent = updated.content || "";
        plainContent = plainContent.replace(/<p>/g, "").replace(/<\/p>/g, "\n\n").replace(/<br>/g, "\n");
        plainContent = plainContent.replace(/<[^>]+>/g, "").trim();

        setForm({
          title: updated.title || form.title,
          excerpt: updated.excerpt || form.excerpt,
          content: plainContent || form.content,
          category: updated.category || form.category,
          image_url: updated.image_url || form.image_url,
          source_url: updated.source_url || form.source_url,
          source_name: updated.source_name || form.source_name,
          status: updated.status || form.status,
        });

        const changes = response.data.changes_applied || [];
        let changeText = "Pronto! Atualizei: " + changes.filter(c => c !== "updated_at").join(", ");
        setChatHistory(prev => [...prev, { role: "ai", text: changeText }]);
        toast.success("Artigo atualizado pelo agente!");
      } else {
        setChatHistory(prev => [...prev, { role: "ai", text: response.data.message || "Não consegui processar. Tente reformular." }]);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erro ao processar. O crédito da IA pode ter acabado.";
      setChatHistory(prev => [...prev, { role: "ai", text: msg }]);
      toast.error("Erro na IA");
    } finally {
      setAiLoading(false);
    }
  };

  const quickActions = [
    { label: "Melhorar título", icon: FileText, instruction: "Melhore o título, deixe mais chamativo e informal para o público jovem" },
    { label: "Expandir texto", icon: Wand2, instruction: "Expanda o conteúdo com mais detalhes, adicione mais 2-3 parágrafos ricos em informação" },
    { label: "Corrigir foto", icon: ImageIcon, instruction: "A foto não corresponde ao artista da matéria. Busque a foto correta do artista mencionado no título" },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-12 h-12 text-[#FF007F] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="back-btn">
            <ArrowLeft size={24} className="text-zinc-400" />
          </button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-white" data-testid="edit-article-title">
              Editor de Matéria
            </h1>
            <p className="text-zinc-500 text-sm">Edite manualmente ou peça ao agente IA</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleDiscard} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white text-sm font-medium rounded-xl transition-all disabled:opacity-50 border border-red-600/30"
              data-testid="discard-btn">
              <Trash2 size={16} /> Descartar
            </button>
            <button onClick={() => handleSubmit("draft")} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-50"
              data-testid="save-draft-btn">
              <Save size={16} /> Salvar Rascunho
            </button>
            <button onClick={() => handleSubmit("published")} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#FF007F] hover:bg-[#FF3399] text-white text-sm font-bold rounded-xl transition-all disabled:opacity-50"
              data-testid="publish-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
              Publicar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Form Editor (3/5) */}
          <div className="lg:col-span-3 space-y-5">
            {/* Image Preview + Title */}
            <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
              {form.image_url && (
                <div className="relative h-52 overflow-hidden">
                  <img src={form.image_url} alt="" className="w-full h-full object-cover object-top" onError={(e) => e.target.style.display = "none"} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                </div>
              )}
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Título</label>
                  <input type="text" name="title" value={form.title} onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white text-lg font-medium placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors"
                    placeholder="Título da matéria" data-testid="title-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Resumo</label>
                  <textarea name="excerpt" value={form.excerpt} onChange={handleChange} rows={2}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors resize-none text-sm"
                    placeholder="Resumo curto" data-testid="excerpt-input" />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-5">
              <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Conteúdo</label>
              <textarea name="content" value={form.content} onChange={handleChange} rows={14}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] transition-colors resize-none text-sm leading-relaxed"
                placeholder="Conteúdo completo da matéria..." data-testid="content-input" />
            </div>

            {/* Meta */}
            <div className="bg-[#121212] rounded-2xl border border-white/10 p-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Categoria</label>
                  <select name="category" value={form.category} onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-[#FF007F]"
                    data-testid="category-select">
                    {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Imagem</label>
                  <div className="flex gap-2">
                    <input type="url" name="image_url" value={form.image_url} onChange={handleChange}
                      className="flex-1 px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#FF007F]"
                      placeholder="URL da imagem" data-testid="image-url-input" />
                    <label className="flex items-center gap-1 px-3 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-zinc-400 hover:border-[#FF007F] cursor-pointer transition-colors">
                      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="image-upload" />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: AI Agent Chat (2/5) */}
          <div className="lg:col-span-2">
            <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden sticky top-20 flex flex-col" style={{ height: "calc(100vh - 120px)" }}>
              {/* Chat Header */}
              <div className="p-4 border-b border-white/10 bg-gradient-to-r from-[#FF007F]/10 to-[#00E5FF]/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00E5FF] flex items-center justify-center">
                    <Bot size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm">Agente Editor</h3>
                    <p className="text-zinc-500 text-xs">Peça correções em linguagem natural</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${aiLoading ? "bg-amber-400 animate-pulse" : "bg-green-500"}`} />
                    <span className="text-xs text-zinc-500">{aiLoading ? "Processando..." : "Online"}</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="p-3 border-b border-white/5 flex flex-wrap gap-2">
                {quickActions.map((action) => (
                  <button key={action.label}
                    onClick={() => { setChatInput(action.instruction); }}
                    disabled={aiLoading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white text-xs font-medium rounded-lg transition-all disabled:opacity-50"
                    data-testid={`quick-${action.label.toLowerCase().replace(/ /g, "-")}`}>
                    <action.icon size={12} />
                    {action.label}
                  </button>
                ))}
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatHistory.length === 0 && (
                  <div className="text-center py-8">
                    <Bot className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-600 text-sm">Olá! Sou o agente editor do arrochaplay.</p>
                    <p className="text-zinc-700 text-xs mt-1">Digite uma instrução como:</p>
                    <div className="mt-3 space-y-1.5 text-left max-w-xs mx-auto">
                      {[
                        '"A foto está errada, busque a do Pablo"',
                        '"Melhore o texto, está muito curto"',
                        '"Mude o título para algo mais chamativo"',
                      ].map((ex, i) => (
                        <p key={`example-${i}`} className="text-zinc-600 text-xs italic bg-white/5 rounded-lg px-3 py-1.5">{ex}</p>
                      ))}
                    </div>
                  </div>
                )}
                {chatHistory.map((msg, i) => (
                  <div key={`msg-${i}-${msg.role}`} className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    {msg.role === "ai" && (
                      <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#00E5FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot size={14} className="text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-[#FF007F]/20 text-white rounded-br-md"
                        : "bg-white/5 text-zinc-300 rounded-bl-md"
                    }`}>
                      {msg.text}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-7 h-7 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User size={14} className="text-zinc-400" />
                      </div>
                    )}
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#00E5FF] flex items-center justify-center flex-shrink-0">
                      <Bot size={14} className="text-white" />
                    </div>
                    <div className="bg-white/5 px-4 py-3 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <div className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-3 border-t border-white/10">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendAiInstruction()}
                    placeholder="Ex: Melhore o título, busque foto do Pablo..."
                    className="flex-1 px-4 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-xl text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#FF007F]"
                    disabled={aiLoading}
                    data-testid="ai-chat-input"
                  />
                  <button
                    onClick={sendAiInstruction}
                    disabled={aiLoading || !chatInput.trim()}
                    className="flex items-center justify-center w-10 h-10 bg-[#FF007F] hover:bg-[#FF3399] disabled:opacity-50 text-white rounded-xl transition-all"
                    data-testid="ai-send-btn"
                  >
                    {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
